import { Components } from '.generated/templates/Feature.Components.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import {
  ImageField,
  LinkField,
  RichTextField,
  Image as SitecoreImage,
  TextField,
} from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import {
  pageorLink,
  NavigationLink as NavigationLinkItem,
} from 'components/authorable/site/Footer/Footer';
import { useCallback, useEffect, useState } from 'react';
import Container from 'components/helpers/Container';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import ArrowDown from 'components/authorable/site/Header/SvgArrowDown/arrow-down';
import Search from 'components/authorable/site/Header/SvgSearch/search';
import NrcSearch from 'components/authorable/site/Header/SvgSearch/nrc-search';
import NrcMenuClose from 'components/authorable/site/Header/SvgMenuClose/nrc-menu-close';
import NrcMenu from 'components/authorable/site/Header/SvgMenu/nrc-menu';
import { useScreenBreakpoint } from 'lib/util/useScreenWidth';
import { FieldValue } from 'components/helpers/SitecoreForm/FieldProps';
import NRCHeaderSkeleton from './NRCHeaderSkeleton';
import UtilityNav from './UtilityNav';
import { IsNetworkSite, IsNmdpConnectSite } from 'lib/util/site-configuration';
import {
  logoNavigationGtmPush,
  menuLocation,
  pageNavigationDataLayerPush,
} from 'lib/util/gtm-datalayer';
import { LinkForNavPageOrLink, NavPageOrLink, NavPageOrLinkWithChildren } from 'lib/util/site-nav';

type navigationHeader = Sitecore.Override.ItemEx &
  Data.Navigation.Fields.NavigationHeader & {
    children?: NavigationLinkItem[];
  };

type NavigationLink = {
  displayName: string | undefined;
  id?: string | undefined;
  url?: string | undefined;
  templateId?: string | undefined;
  name?: string | undefined;
  fields: {
    navigationHeading: {
      value: string;
    };
    navigationItemLink?: LinkField | undefined;
  };
  children?: NavigationLink[];
};

type headerOrLink = (NavigationLink | navigationHeader) & { templateId: string };

type AccountDropdownItem = {
  id: string;
  displayName: string;
  fields: {
    Action: LinkField;
    Title: TextField;
  };
};

type ProfileItem = {
  loginLink?: {
    value?: {
      text?: string;
    };
  };
  createAccountLinkText?: {
    value?: string;
  };
  createAccountMessage?: RichTextField;
  accountDropdownLinks?: AccountDropdownItem[];
  accountLinkText?: FieldValue;
};

export type NrcHeaderProps = {
  fields?: {
    root?: Components.Site.Fields.Header & {
      utilityLinks?: pageorLink[];
      profileItem?: ProfileItem[];
      children?: headerOrLink[] & {
        children?: Array<NavPageOrLink | NavPageOrLinkWithChildren>;
      };
      id: string;
      fields: { searchPlaceholderText?: FieldValue };
    };
  };
};

interface CollectionStackItem {
  linkItems: NavigationLink[];
  backToDisplayName: string | undefined;
}

type MobileTopContainerProps = {
  toggleMobileMenu: () => void;
  toggleSearchMobileMenu: () => void;
  isOpen: boolean;
  searchIsOpen: boolean;
  logo: ImageField;
  logoText: string;
};

type DesktopTopContainerProps = {
  profileItem: ProfileItem;
  placeholderText: string;
  logo: ImageField;
  utilityLinks: pageorLink[];
  logoText: string;
};

interface DesktopNavigationItemProps {
  item: NavigationLink;
  openSubMenuId: string | null;
  toggleSubMenu: (navId: string | null, displayText: string) => void;
}

const borderMidGray = `border-gray-midGray`;
const fontMedium = `font-medium`;
const textWhite = `text-white`;
const transitionTransform = `transition-transform`;
const xlMxAuto = `xl:mx-auto`;
const inlineBlock = `inline-block`;
const svgArrowDown = `svg-arrow-down`;
const xlFloatNone = `xl:float-none`;
const floatRight = `float-right`;
const textBlueNavy = `text-blue-navy`;
const hoverTextBlueNavy = `hover:text-blue-navy`;
const maxWScreen2xl = `max-w-screen-2xl`;

const menuBar = classnames(
  'border-0',
  fontMedium,
  'justify-evenly',
  'justify-start',
  'leading-3',
  'text-base',
  textWhite,
  'xl:flex'
);

const dropMenuItem = classnames('block', 'w-full', 'p-4', 'xl:py-2', 'xl:px-0', 'xl:w-fit');
const hasSubmenu = classnames('border-b', borderMidGray, 'has-submenu', 'relative', 'xl:border-0');

const subMenu = classnames(
  'absolute',
  'bg-white',
  borderMidGray,
  maxWScreen2xl,
  'mx-auto',
  'pb-2',
  'xl:border-b',
  'xl:break-inside-avoid-column',
  'xl:flex-col',
  'xl:px-10',
  'xl:py-4',
  'xl:shadow-lg',
  'xl:left-[-24px]',
  'xl:min-w-[368px]',
  'xl:w-max',
  'z-20'
);

const desktopMainNav = classnames(
  'absolute',
  'bg-white',
  'h-full',
  transitionTransform,
  'w-full',
  'xl:bg-blue-navy',
  xlMxAuto,
  'xl:relative',
  'xl:transition-none',
  'z-30'
);

const animateNavOpen = classnames('translate-x-0', 'left-0');
const animateNavClose = classnames('-translate-x-full', 'left-[-100%]', 'xl:left-[100%]');
const megaMenu = classnames(
  'absolute',
  'bg-white',
  'left-0',
  maxWScreen2xl,
  'mx-auto',
  'right-0',
  'w-screen',
  'xl:border-b',
  'xl:border-gray-midGray',
  'xl:pb-12',
  'xl:pl-[77px]',
  'xl:px-10',
  'xl:px-20',
  'xl:py-1.5',
  'xl:pt-6',
  'xl:shadow-lg'
);

const mobileMainNav = classnames(
  'absolute',
  'bg-white',
  'h-full',
  transitionTransform,
  'w-full',
  'z-30'
);

const desktopSearch = classnames(
  'aa-IconWrapper',
  'absolute',
  'h-full',
  'top-[100px]',
  transitionTransform,
  'w-full',
  'xl:hidden',
  'z-30'
);

const backBtnClass = classnames(
  'back-btn',
  dropMenuItem,
  'bg-blue-navy',
  'block',
  'border-b',
  fontMedium,
  'py-2.5',
  'text-center',
  textWhite,
  'xl:hidden'
);

const mobileBtn = classnames(
  dropMenuItem,
  'xl:hidden',
  'block',
  fontMedium,
  'text-left',
  textWhite
);

const backBtnArrow = classnames(
  'float-left',
  inlineBlock,
  '-ml-2',
  'rotate-90',
  svgArrowDown,
  xlFloatNone
);

const mobileArrow = classnames(
  '-rotate-90',
  floatRight,
  inlineBlock,
  'ml-2',
  svgArrowDown,
  xlFloatNone
);

const desktopArrow = classnames(floatRight, inlineBlock, 'ml-2', svgArrowDown, xlFloatNone);

const getItemDisplayName = (item: NavigationLink): string => {
  const navHeadingValue = item?.fields?.navigationHeading?.value?.trim() ?? '';
  return navHeadingValue.length > 0 ? navHeadingValue : item?.displayName?.trim() ?? '';
};

const MobileTopContainer = ({
  toggleMobileMenu,
  toggleSearchMobileMenu,
  isOpen,
  searchIsOpen,
  logo,
  logoText,
}: MobileTopContainerProps): JSX.Element => {
  return (
    <div className={classnames('xl:hidden', 'xl:block', 'border-b-2', 'border-blue-navy')}>
      <div className={classnames('flex', 'justify-between', 'items-center', 'pt-5')}>
        <div className={classnames('self-start')}>
          <button
            type="button"
            aria-label="Toggle navigation menu"
            className={classnames(
              'bg-white',
              'flex',
              'flex-row',
              floatRight,
              'h-auto',
              'mb-2',
              'mx-2',
              'relative',
              'z-20'
            )}
            data-id="header-mobile-menu-toggle"
            onClick={toggleMobileMenu}
          >
            <div
              className={classnames(
                'align-middle',
                'flex',
                'flex-col-reverse',
                'font-semibold',
                hoverTextBlueNavy,
                'justify-between',
                'text-blue',
                'text-body',
                'w-full'
              )}
            >
              {isOpen ? (
                <span
                  className={classnames('leading-2', 'px-2.5', 'pt-1', textBlueNavy, 'text-xs')}
                >
                  Close
                </span>
              ) : (
                <span
                  className={classnames('leading-2', 'px-2.5', 'pt-1', textBlueNavy, 'text-xs')}
                >
                  Menu
                </span>
              )}
              {isOpen ? (
                <NrcMenuClose className={classnames('mx-auto')} />
              ) : (
                <NrcMenu className={classnames('mx-auto')} />
              )}
            </div>
          </button>
        </div>
        <div className={classnames('self-start', 'flex')}>
          <NextLink href="/">
            <a
              href="/"
              aria-label="Logo link"
              onClick={() => {
                logoNavigationGtmPush(menuLocation[0]);
              }}
            >
              <SitecoreImage field={logo} className={classnames('h-10', 'w-full')} />
            </a>
          </NextLink>
          {logoText !== '' && (
            <div className={classnames('flex', 'items-center', 'ml-1')}>
              <NextLink href="/">
                <a
                  href="/"
                  className={classnames(
                    'text-blue-navy',
                    'font-bold',
                    'font-dmSans',
                    'text-m',
                    'sm:text-xl'
                  )}
                >
                  {logoText}
                </a>
              </NextLink>
            </div>
          )}
        </div>
        <div className={classnames('self-start')}>
          <button
            type="button"
            aria-label="Search"
            className={classnames('bg-white', 'flex', 'flex-row', floatRight, 'relative', 'z-20')}
            data-id="Search"
            onClick={toggleSearchMobileMenu}
          >
            <div
              className={classnames(
                'align-middle',
                'flex',
                'flex-col-reverse',
                'font-semibold',
                hoverTextBlueNavy,
                'justify-between',
                'pr-3',
                'text-blue',
                'text-body',
                'w-full'
              )}
            >
              {searchIsOpen ? (
                <>
                  <span className={classnames('px-2.5', 'leading-2', 'text-xs', textBlueNavy)}>
                    Search
                  </span>
                  <NrcMenuClose className={classnames('mx-auto')} />
                </>
              ) : (
                <>
                  <span className={classnames('px-2.5', 'leading-2', 'text-xs', textBlueNavy)}>
                    Search
                  </span>
                  <NrcSearch className={classnames('mx-auto')} />
                </>
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

const DesktopTopContainer = ({
  logo,
  profileItem,
  placeholderText,
  utilityLinks,
  logoText,
}: DesktopTopContainerProps): JSX.Element => {
  return (
    <nav
      aria-label="Network Resource Center utility navigation"
      data-navigation="utility"
      className={classnames(
        'absolute',
        'flex-col',
        'hidden',
        'max-w-full',
        'md:my-4',
        'w-full',
        'xl:flex',
        'xl:flex-row',
        'xl:h-12',
        'xl:items-center',
        xlMxAuto,
        'xl:relative'
      )}
    >
      <Container
        size="standard"
        className={classnames(
          'flex',
          'flex-col',
          'w-full',
          'xl:flex-row',
          'xl:h-12',
          'xl:items-center',
          'xl:justify-between',
          xlMxAuto,
          'xl:pl-20',
          'xl:pr-8'
        )}
      >
        <div className={classnames('flex')}>
          <NextLink href="/">
            <a
              href="/"
              aria-label="Logo link"
              onClick={() => {
                logoNavigationGtmPush(menuLocation[0]);
              }}
            >
              <SitecoreImage field={logo} />
            </a>
          </NextLink>
          {logoText !== '' && (
            <div className={classnames('flex', 'items-center', 'ml-2')}>
              <NextLink href="/">
                <a
                  href="/"
                  className={classnames('text-blue-navy', 'font-bold', 'font-dmSans', 'text-xl')}
                >
                  {logoText}
                </a>
              </NextLink>
            </div>
          )}
        </div>
        <div
          className={classnames(
            'relative',
            'bg-gradient-to-r',
            'from-blue',
            'to-green',
            'p-0',
            'xl:bg-none',
            'xl:pr-2.5'
          )}
        >
          <Search searchplaceholderText={placeholderText} />
        </div>
        {IsNetworkSite() ? (
          <UtilityNav profileItem={profileItem} />
        ) : (
          <ul
            role="menu"
            className={classnames(
              'hidden',
              'xl:flex',
              'xl:top-auto',
              'xl:relative',
              'xl:flex-row',
              'justify-end'
            )}
          >
            {utilityLinks?.map((item) => (
              <li
                role="menuitem"
                key={item.name}
                className={classnames('list-none')}
                aria-label="Menu item"
              >
                <LinkForNavPageOrLink
                  className={classnames(
                    'block',
                    'font-semibold',
                    'leading-3',
                    'xl:text-sm',
                    'xl:leading-3',
                    'xl:px-4',
                    'hover:underline',
                    'text-blue-navy'
                  )}
                  item={item}
                />
              </li>
            ))}
          </ul>
        )}
      </Container>
    </nav>
  );
};

const DesktopNavigation = ({
  isOpen,
  desktopItems,
}: {
  isOpen: boolean;
  desktopItems: React.ReactNode;
}) => {
  return (
    <nav
      aria-label="Network Resource Center main navigation"
      data-navigation="main"
      className={classnames(desktopMainNav, isOpen ? animateNavOpen : animateNavClose)}
    >
      <ul
        className={classnames(menuBar, maxWScreen2xl, xlMxAuto, 'xl:pl-[48px]')}
        role="menubar"
        aria-label="Network Resource Center main menu"
      >
        {desktopItems}
      </ul>
    </nav>
  );
};

const renderNavigationLink = (
  item: NavigationLink,
  toggleSubMenu: (navId: string | null, displayText: string) => void
) => {
  return (
    <li
      role="menuitem"
      key={item.id}
      className={classnames('bg-white', 'block', 'border-b', 'border-gray-quartz')}
      aria-label="Menu item"
    >
      <LinkForNavPageOrLink
        onClick={() => toggleSubMenu(`nav-item--${item.id}`, getItemDisplayName(item))}
        className={classnames(dropMenuItem)}
        item={item as NavPageOrLink}
      />
    </li>
  );
};

const getItemsForDesktop = (
  toggleSubMenu: (navId: string | null, displayText: string) => void,
  orgLinkItems: NavigationLink[] = []
) => {
  // eslint-disable-next-line array-callback-return, consistent-return
  return orgLinkItems?.map((item: NavigationLink) => {
    // If the item has a navigation link, return a list item with a link
    if (item.fields?.navigationItemLink) {
      return renderNavigationLink(item, toggleSubMenu);
    }

    // Desktop - If renderChildren is available and the item has children, render child items
    if (item.children) {
      return (
        <div key={item.id} className={classnames({ 'h-44': IsNmdpConnectSite() })}>
          <h6
            className={classnames(
              'font-bold',
              'hidden',
              'xl:block',
              'xl:text-black',
              'xl:uppercase'
            )}
          >
            {getItemDisplayName(item)}
          </h6>
          {item.children?.map((link: NavigationLink) => {
            return renderNavigationLink(link, toggleSubMenu);
          })}
        </div>
      );
    }
    return null;
  });
};

const DesktopNavigationItem: React.FC<DesktopNavigationItemProps> = ({
  item,
  openSubMenuId,
  toggleSubMenu,
}) => {
  const isSubMenuOpen = openSubMenuId === `nav-item--${item?.id}`;
  let navCount = (item.children || []).reduce(
    (acc, item2) => acc + (item2?.children?.length || 0),
    0
  );
  const menuClasses = navCount !== 0 ? megaMenu : subMenu;
  const displayText = getItemDisplayName(item);

  const getColsForDesktop = (orgLinkItems: NavigationLink[] = [], count: number | null = 0) => {
    if (count === 0) {
      return getItemsForDesktop(toggleSubMenu, orgLinkItems);
    }

    const colEndArray = [] as Array<number>;

    if (orgLinkItems.length > 4) {
      navCount = count ?? 0;
      const idealItemsPerGroup = Math.round(navCount / 4);
      let currentCount = 0;
      let currentColIndex = 0;
      let linksCounter = 0;

      for (let i = 0; i < orgLinkItems.length; i++) {
        currentCount++;
        colEndArray[currentColIndex] = currentCount;

        const children = orgLinkItems.at(i)?.children;
        const childrenLength = children ? children.length : 0;
        linksCounter += childrenLength;

        if (linksCounter >= idealItemsPerGroup && currentColIndex <= 3) {
          currentColIndex++;
          linksCounter = 0;
        }
      }
    } else {
      colEndArray[0] = 1;
      colEndArray[1] = 2;
      colEndArray[2] = 3;
      colEndArray[3] = 4;
    }

    return (
      <>
        <div className={classnames('xl:w-[25%]')}>
          {getItemsForDesktop(toggleSubMenu, orgLinkItems.slice(0, colEndArray[0]))}
        </div>
        <div className={classnames('xl:w-[25%]')}>
          {getItemsForDesktop(toggleSubMenu, orgLinkItems.slice(colEndArray[0], colEndArray[1]))}
        </div>
        <div className={classnames('xl:w-[25%]')}>
          {getItemsForDesktop(toggleSubMenu, orgLinkItems.slice(colEndArray[1], colEndArray[2]))}
        </div>
        <div className={classnames('xl:w-[25%]')}>
          {getItemsForDesktop(toggleSubMenu, orgLinkItems.slice(colEndArray[2], colEndArray[3]))}
        </div>
      </>
    );
  };

  return (
    <>
      {item.children && item.children?.length > 0 && (
        <li
          role="menuitem"
          key={item.id}
          id={`nav-item--${item.id}`}
          className={classnames({
            'submenu-open': isSubMenuOpen,
            relative: navCount === 0,
          })}
        >
          <button
            onClick={() => toggleSubMenu(`nav-item--${item.id}`, displayText)}
            type="button"
            className={classnames('xl:p-4', fontMedium)}
          >
            <div className={classnames('max-h-[25px]')}>
              {displayText}
              <ArrowDown aria-hidden="true" className={classnames(desktopArrow)} />
            </div>
          </button>
          <ul role="menu" className={menuClasses}>
            {getColsForDesktop(item.children, navCount)}
          </ul>
        </li>
      )}
      {item.fields?.navigationItemLink && (
        <li
          role="menuitem"
          key={item.id}
          className={classnames(
            {
              relative: navCount === 0,
            },
            'nav-direct-link'
          )}
          aria-label="Menu item"
        >
          <div className={classnames('xl:p-4', fontMedium)}>
            <div className={classnames('max-h-[25px]')}>
              <LinkForNavPageOrLink
                className={classnames('text-white', 'font-semibold')}
                item={item as NavPageOrLink}
              />
            </div>
          </div>
        </li>
      )}
    </>
  );
};

const MobileNavigation = ({
  isOpen,
  collectionStack,
  getBackButton,
  mobileItems,
  searchIsOpen,
  profileItem,
  placeholderText,
  utilityLinks,
}: {
  isOpen: boolean;
  collectionStack: CollectionStackItem[];
  getBackButton: () => React.ReactNode;
  mobileItems: React.ReactNode;
  searchIsOpen: boolean;
  profileItem: ProfileItem | undefined;
  placeholderText: string;
  utilityLinks: pageorLink[];
}) => {
  return (
    <>
      <nav
        className={classnames(
          mobileMainNav,
          isOpen ? animateNavOpen : animateNavClose,
          collectionStack.length > 1 ? 'state-open' : null
        )}
      >
        {getBackButton()}
        <ul role="menubar">{mobileItems}</ul>
        {IsNetworkSite() ? (
          <UtilityNav profileItem={profileItem} />
        ) : (
          <ul
            role="menu"
            className={classnames(
              'flex',
              'flex-col',
              'w-full',
              'px-4',
              'text-white',
              'justify-end',
              'border-t-[24px]',
              'border-gray-whisper'
            )}
          >
            {utilityLinks?.map((item) => (
              <li
                role="menuitem"
                key={item.name}
                className={classnames('py-3')}
                aria-label="Menu item"
              >
                <LinkForNavPageOrLink
                  className={classnames(
                    'block',
                    'font-semibold',
                    'leading-3',
                    'hover:underline',
                    'text-blue-navy',
                    'xl:pr-5'
                  )}
                  item={item}
                />
              </li>
            ))}
          </ul>
        )}
      </nav>
      <div
        className={classnames(
          desktopSearch,
          searchIsOpen ? animateNavOpen : animateNavClose,
          'mobile-nav-search'
        )}
      >
        <Search searchplaceholderText={placeholderText} />
        <div className={classnames('bg-black', 'h-full', 'opacity-50')} />
      </div>
    </>
  );
};

const NrcHeader = ({ fields }: NrcHeaderProps): JSX.Element => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [openSubMenuId, setOpenSubMenuId] = useState<string | null>(null);
  const [shouldKeepSubMenuOpen, setShouldKeepSubMenuOpen] = useState(false); // State to track whether submenu should stay open
  const [collectionStack, setCollectionStack] = useState<CollectionStackItem[]>([]);

  // Use the new useScreenBreakpoint hook
  const { isLargeScreen, isInitializing } = useScreenBreakpoint(); // >= 1280px exact match
  const utilityLinks = fields?.root?.fields?.utilityLinks as pageorLink[];
  const logoText =
    fields?.root?.fields?.logoText.value !== undefined &&
    fields?.root?.fields?.logoText.value !== ''
      ? fields?.root?.fields?.logoText.value
      : '';

  const toggleSubMenu = useCallback(
    (navId: string | null, displayText: string) => {
      if (openSubMenuId === navId) {
        setOpenSubMenuId(null);
        setShouldKeepSubMenuOpen(false);
        pageNavigationDataLayerPush('Search', menuLocation[0]);
      } else {
        setOpenSubMenuId(navId);
        setShouldKeepSubMenuOpen(true);
        pageNavigationDataLayerPush(displayText, menuLocation[1]);
      }
    },
    [openSubMenuId]
  );

  useEffect(() => {
    const handleOutsideNavClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-navigation="main"]')) {
        toggleSubMenu(null, '');
      }
    };
    document.addEventListener('click', handleOutsideNavClick);
    return () => {
      document.removeEventListener('click', handleOutsideNavClick);
    };
  }, [shouldKeepSubMenuOpen, openSubMenuId, toggleSubMenu]);

  useEffect(() => {
    setIsOpen(false);
    setOpenSubMenuId(null);
    document.body.className = 'show-body';
  }, [router?.asPath]);

  useEffect(() => {
    setCollectionStack([
      {
        linkItems: fields?.root?.children as NavigationLink[],
        backToDisplayName: '',
      },
    ]);
  }, [fields?.root?.children]);

  const root = fields?.root;
  const logo = fields?.root?.fields?.logo as ImageField;
  const placeholderText = fields?.root?.fields.searchPlaceholderText?.value as string;
  const profileItem = (root?.fields?.profileItem?.fields as ProfileItem) || undefined;
  if (!fields) {
    return <MissingDataSource />;
  }

  // Show loading skeleton while initializing to prevent layout shift
  if (isInitializing) {
    return <NRCHeaderSkeleton />;
  }
  const toggleMobileMenu = () => {
    setIsOpen((prev) => !prev);
    document.body.className = isOpen ? 'show-body' : 'hide-body';
    setSearchIsOpen(false);
    setCollectionStack([
      {
        linkItems: root?.children as NavigationLink[],
        backToDisplayName: '',
      },
    ]);
    setOpenSubMenuId(null);
    const displayText = isOpen ? 'Close' : 'Menu';
    pageNavigationDataLayerPush(displayText, menuLocation[0]);
  };
  const toggleSearchMobileMenu = () => {
    setSearchIsOpen((prev) => !prev);
    setIsOpen(false);
    document.body.className = 'show-body';
    pageNavigationDataLayerPush('Search', menuLocation[0]);
  };

  const getMobileItems = (orgLinkItems: NavigationLink[] = []) => {
    return orgLinkItems?.map((item: NavigationLink) => {
      const onItemClick = () => {
        const displayName = getItemDisplayName(item);
        if (item.children) {
          const newItems: CollectionStackItem[] = [
            ...collectionStack,
            {
              linkItems: item.children,
              backToDisplayName: displayName,
            },
          ];
          setCollectionStack(newItems);
        }
        pageNavigationDataLayerPush(displayName, menuLocation[0]);
      };
      if (item.fields?.navigationItemLink) {
        return (
          <li
            role="menuitem"
            key={item.id}
            className={classnames(
              dropMenuItem,
              'bg-blue-navy',
              'block',
              'border-b',
              borderMidGray,
              'nav-direct-link'
            )}
            aria-label="Menu item"
          >
            <LinkForNavPageOrLink item={item as NavPageOrLink} onClick={toggleMobileMenu} />
          </li>
        );
      }
      return (
        <li role="menuitem" key={item.id} className={classnames(hasSubmenu)}>
          <button onClick={onItemClick} type="button" className={classnames(mobileBtn)}>
            <div>
              {getItemDisplayName(item)}
              <ArrowDown aria-hidden="true" className={classnames(mobileArrow)} />
            </div>
          </button>
        </li>
      );
    });
  };
  const getDesktopItems = (_orgLinkItems: NavigationLink[]) => {
    return (_orgLinkItems || []).map((item: NavigationLink) => (
      <DesktopNavigationItem
        key={item.id}
        item={item}
        openSubMenuId={openSubMenuId}
        toggleSubMenu={toggleSubMenu}
      />
    ));
  };
  const getBackButton = () => {
    const backHandler = () => {
      const backItems = collectionStack.reduce(
        (acc: CollectionStackItem[], item: CollectionStackItem, inx: number) => {
          if (inx < collectionStack.length - 1) {
            acc.push(item);
          }
          return acc;
        },
        []
      );
      setCollectionStack(backItems);
    };
    if (collectionStack.length > 1) {
      const backButtonText = collectionStack[collectionStack.length - 1].backToDisplayName || '';
      return (
        <button type="button" onClick={backHandler} className={classnames(backBtnClass)}>
          <div>
            <ArrowDown aria-hidden="true" className={classnames(backBtnArrow)} />
            {backButtonText}
          </div>
        </button>
      );
    }
    return <></>;
  };
  const lastItem = collectionStack[collectionStack.length - 1];
  const mobileItems = getMobileItems(lastItem?.linkItems);
  const desktopItems = getDesktopItems(collectionStack[0]?.linkItems);
  return (
    <>
      {!isLargeScreen && (
        <MobileTopContainer
          toggleMobileMenu={toggleMobileMenu}
          toggleSearchMobileMenu={toggleSearchMobileMenu}
          isOpen={isOpen}
          searchIsOpen={searchIsOpen}
          logo={logo}
          logoText={logoText}
        />
      )}
      <div className={classnames('xl:block')}>
        <DesktopTopContainer
          profileItem={profileItem}
          placeholderText={placeholderText}
          logo={logo}
          utilityLinks={utilityLinks}
          logoText={logoText}
        />
        {isLargeScreen && <DesktopNavigation isOpen={isOpen} desktopItems={desktopItems} />}
        {!isLargeScreen && (
          <MobileNavigation
            isOpen={isOpen}
            collectionStack={collectionStack}
            getBackButton={getBackButton}
            mobileItems={mobileItems}
            searchIsOpen={searchIsOpen}
            profileItem={profileItem}
            placeholderText={placeholderText}
            utilityLinks={utilityLinks}
          />
        )}
      </div>
    </>
  );
};

export default NrcHeader;
